import React, { useEffect, useState } from "react";
import { Pod24AdminLayout } from "../../components/Pod24Admin/Pod24AdminLayout";
import { OID_PRESETS, OID_PRESET_SOURCE_COUNT } from "./oidPresets";
import { adminMachineProductUseCase } from "../../../di/container";
import { Button } from "../../components/UI/Button";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
  CreateMachineProductRequest,
  MachineProductEntity,
  MachineType,
  Manufacturer,
  MachineProtocol,
} from "../../../domain/types/machine-product";
import NotificationsList from "../../components/UI/NotificationList";
import { useNotifications } from "../../hooks/use-notifications";

const MACHINE_TYPES: { value: MachineType; label: string }[] = [
  { value: "PRINTER", label: "프린터" },
  { value: "CUTTER", label: "커터" },
  { value: "PLOTTER", label: "플로터" },
];

const MANUFACTURERS: { value: Manufacturer; label: string }[] = [
  { value: "KONICA_MINOLTA", label: "Konica Minolta" },
  { value: "FUJI_XEROX", label: "Fuji Xerox" },
  { value: "FUJI_FILM", label: "Fuji Film" },
  { value: "RICOH", label: "Ricoh" },
  { value: "CANON", label: "Canon" },
  { value: "HP", label: "HP" },
  { value: "EPSON", label: "Epson" },
  { value: "DAEHO", label: "대호" },
  { value: "OKI", label: "OKI" },
  { value: "SINDOH", label: "신도리코" },
  { value: "OCE", label: "OCE" },
  { value: "DUPLO", label: "듀플로" },
];

const PROTOCOLS: { value: MachineProtocol; label: string }[] = [
  { value: "SNMP", label: "SNMP" },
  { value: "FIERY", label: "Fiery" },
  { value: "DAEHO", label: "대호" },
  { value: "SHARED_FOLDER", label: "공유 폴더" },
];

interface FormData {
  machineType: MachineType;
  manufacturer: Manufacturer;
  machineName: string;
  machineDescription: string;
  protocol: MachineProtocol;
  machineStatusCommand: string;
  machineRuntimeCommand: string;
  machineTotalWorkloadCommand: string;
  suppliesNameCommand: string;
  suppliesTypeCommand: string;
  suppliesMaxLevelCommand: string;
  suppliesCurrentLevelCommand: string;
  trayNameCommand: string;
  paperNameCommand: string;
  paperWidthCommand: string;
  paperHeightCommand: string;
  trayMaxCapacityCommand: string;
  trayCurrentCapacityCommand: string;
  jobNameCommand: string;
  jobStartTimeCommand: string;
  jobEndTimeCommand: string;
  jobStatusCommand: string;
  alertSeverityCommand: string;
  alertGroupCommand: string;
  alertCodeCommand: string;
  alertDescriptionCommand: string;
  alertTimeCommand: string;
  errorStateCommand: string;
}

const DEFAULT_FORM: FormData = {
  machineType: "PRINTER",
  manufacturer: "KONICA_MINOLTA",
  machineName: "",
  machineDescription: "",
  protocol: "SNMP",
  machineStatusCommand: "",
  machineRuntimeCommand: "",
  machineTotalWorkloadCommand: "",
  suppliesNameCommand: "",
  suppliesTypeCommand: "",
  suppliesMaxLevelCommand: "",
  suppliesCurrentLevelCommand: "",
  trayNameCommand: "",
  paperNameCommand: "",
  paperWidthCommand: "",
  paperHeightCommand: "",
  trayMaxCapacityCommand: "",
  trayCurrentCapacityCommand: "",
  jobNameCommand: "",
  jobStartTimeCommand: "",
  jobEndTimeCommand: "",
  jobStatusCommand: "",
  alertSeverityCommand: "",
  alertGroupCommand: "",
  alertCodeCommand: "",
  alertDescriptionCommand: "",
  alertTimeCommand: "",
  errorStateCommand: "",
};

const FIELD_GROUPS = [
  {
    title: "기본 정보",
    fields: [] as string[], // handled separately
  },
  {
    title: "상태 명령어",
    fields: [
      "machineStatusCommand",
      "machineRuntimeCommand",
      "machineTotalWorkloadCommand",
    ],
  },
  {
    title: "소모품 명령어",
    fields: [
      "suppliesNameCommand",
      "suppliesTypeCommand",
      "suppliesMaxLevelCommand",
      "suppliesCurrentLevelCommand",
    ],
  },
  {
    title: "용지함/용지 명령어",
    fields: [
      "trayNameCommand",
      "paperNameCommand",
      "paperWidthCommand",
      "paperHeightCommand",
      "trayMaxCapacityCommand",
      "trayCurrentCapacityCommand",
    ],
  },
  {
    title: "작업 명령어",
    fields: [
      "jobNameCommand",
      "jobStartTimeCommand",
      "jobEndTimeCommand",
      "jobStatusCommand",
    ],
  },
  {
    title: "알림 명령어",
    fields: [
      "alertSeverityCommand",
      "alertGroupCommand",
      "alertCodeCommand",
      "alertDescriptionCommand",
      "alertTimeCommand",
    ],
  },
  {
    title: "오류 명령어",
    fields: ["errorStateCommand"],
  },
];

/** 기본 정보를 뺀 나머지 = OID 입력 칸 전체. 추천값을 채울 대상이다. */
const OID_FIELDS: string[] = FIELD_GROUPS.slice(1).flatMap((g) => g.fields);

/** 알림 문구에 쓸 제조사 한글 이름. 모르는 값이면 코드를 그대로 보여준다. */
const MANUFACTURER_LABELS: Record<string, string> = Object.fromEntries(
  MANUFACTURERS.map((m) => [m.value, m.label]),
);

const manufacturerLabel = (value: string) => MANUFACTURER_LABELS[value] ?? value;

/**
 * OID 를 쓰지 않는 프로토콜과 그 이유.
 *
 * SNMP 만 OID 로 장비를 읽는다. 나머지는 각자의 방식이 있어 OID 입력 그룹을 감추고
 * 왜 비어 있는지 알려준다.
 */
const NO_OID_REASONS: Record<string, string> = {
  FIERY:
    "Fiery 프로토콜은 Fiery 공식 API 로 장비를 읽습니다. 별도의 OID 명령어가 필요하지 않습니다.",
  DAEHO:
    "대호 프로토콜은 재단기가 여는 REST API(9288 포트)로 장비를 읽습니다. 별도의 OID 명령어가 필요하지 않습니다.",
  SHARED_FOLDER:
    "공유 폴더 프로토콜은 장비에 접속하지 않고, 장비가 라우터 공유 폴더에 저장한 로그 파일을 읽어옵니다. 별도의 OID 명령어가 필요하지 않습니다.",
};

const FIELD_LABELS: Record<string, string> = {
  machineStatusCommand: "장비 상태",
  machineRuntimeCommand: "가동 시간",
  machineTotalWorkloadCommand: "총 작업량",
  suppliesNameCommand: "소모품 이름",
  suppliesTypeCommand: "소모품 유형",
  suppliesMaxLevelCommand: "소모품 최대 수준",
  suppliesCurrentLevelCommand: "소모품 현재 수준",
  trayNameCommand: "용지함 이름",
  paperNameCommand: "용지 이름",
  paperWidthCommand: "용지 너비",
  paperHeightCommand: "용지 높이",
  trayMaxCapacityCommand: "용지함 최대 용량",
  trayCurrentCapacityCommand: "용지함 현재 용량",
  jobNameCommand: "작업 이름",
  jobStartTimeCommand: "작업 시작 시간",
  jobEndTimeCommand: "작업 종료 시간",
  jobStatusCommand: "작업 상태",
  alertSeverityCommand: "알림 심각도",
  alertGroupCommand: "알림 그룹",
  alertCodeCommand: "알림 코드",
  alertDescriptionCommand: "알림 설명",
  alertTimeCommand: "알림 시간",
  errorStateCommand: "오류 상태",
};

const MachineProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;

  const [form, setForm] = useState<FormData>(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editEnabled, setEditEnabled] = useState(!isEdit);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(["기본 정보"]);
  const { notifications, addItem: addNotification } = useNotifications();

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      adminMachineProductUseCase
        .getById(Number(id))
        .then((product: MachineProductEntity) => {
          setForm({
            machineType: product.machineType,
            manufacturer: product.manufacturer,
            machineName: product.machineName || "",
            machineDescription: product.machineDescription || "",
            protocol: product.protocol,
            machineStatusCommand: product.machineStatusCommand || "",
            machineRuntimeCommand: product.machineRuntimeCommand || "",
            machineTotalWorkloadCommand:
              product.machineTotalWorkloadCommand || "",
            suppliesNameCommand: product.suppliesNameCommand || "",
            suppliesTypeCommand: product.suppliesTypeCommand || "",
            suppliesMaxLevelCommand: product.suppliesMaxLevelCommand || "",
            suppliesCurrentLevelCommand:
              product.suppliesCurrentLevelCommand || "",
            trayNameCommand: product.trayNameCommand || "",
            paperNameCommand: product.paperNameCommand || "",
            paperWidthCommand: product.paperWidthCommand || "",
            paperHeightCommand: product.paperHeightCommand || "",
            trayMaxCapacityCommand: product.trayMaxCapacityCommand || "",
            trayCurrentCapacityCommand:
              product.trayCurrentCapacityCommand || "",
            jobNameCommand: product.jobNameCommand || "",
            jobStartTimeCommand: product.jobStartTimeCommand || "",
            jobEndTimeCommand: product.jobEndTimeCommand || "",
            jobStatusCommand: product.jobStatusCommand || "",
            alertSeverityCommand: product.alertSeverityCommand || "",
            alertGroupCommand: product.alertGroupCommand || "",
            alertCodeCommand: product.alertCodeCommand || "",
            alertDescriptionCommand: product.alertDescriptionCommand || "",
            alertTimeCommand: product.alertTimeCommand || "",
            errorStateCommand: product.errorStateCommand || "",
          });
          // 수정 모드에서는 모든 그룹 펼침
          setExpandedGroups(FIELD_GROUPS.map((g) => g.title));
        })
        .catch((error: Error) => {
          console.error("장비 제품 조회 실패:", error);
          addNotification("장비 제품 조회에 실패했습니다.");
        })
        .finally(() => setLoading(false));
    } else {
      // URL params에서 미보고 장비 정보 가져오기
      const machineName = searchParams.get("machineName");
      const machineDescription = searchParams.get("machineDescription");
      if (machineName || machineDescription) {
        setForm((prev) => ({
          ...prev,
          machineName: machineName || "",
          machineDescription: machineDescription || "",
        }));
      }
    }
  }, [id, isEdit, searchParams]);

  const toggleGroup = (title: string) => {
    setExpandedGroups((prev) =>
      prev.includes(title)
        ? prev.filter((t) => t !== title)
        : [...prev, title]
    );
  };

  const handleChange = (field: keyof FormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * 제조사 추천값으로 채울 OID 를 고른다.
   *
   * `onlyEmpty` 면 비어 있는 칸만 고른다. 제조사를 잘못 골랐다가 되돌릴 때
   * 공들여 넣은 OID 가 날아가면 안 되기 때문이다.
   */
  const presetPatch = (
    manufacturer: string,
    current: FormData,
    onlyEmpty: boolean,
  ): Record<string, string> => {
    const preset = OID_PRESETS[manufacturer as Manufacturer];
    if (!preset) return {};

    const values = current as unknown as Record<string, string>;
    return Object.fromEntries(
      OID_FIELDS.filter(
        (field) => preset[field] && (!onlyEmpty || !values[field]),
      ).map((field) => [field, preset[field]]),
    );
  };

  /** 제조사를 고르면 비어 있는 OID 칸을 추천값으로 채운다. */
  const handleManufacturerChange = (value: string) => {
    setForm((prev) => {
      const next = { ...prev, manufacturer: value as Manufacturer };
      const patch = presetPatch(value, next, true);

      const filled = Object.keys(patch).length;
      if (filled > 0) {
        addNotification(
          `${manufacturerLabel(value)} 추천 OID ${filled}개를 채웠습니다. 확인 후 고쳐주세요.`,
        );
      }
      return { ...next, ...patch };
    });
  };

  /** 추천값으로 전부 덮어쓴다. 적어둔 값도 바뀐다. */
  const applyPresetOverwrite = () => {
    const patch = presetPatch(form.manufacturer, form, false);
    if (Object.keys(patch).length === 0) return;

    setForm((prev) => ({ ...prev, ...patch }));
    addNotification(
      `${manufacturerLabel(form.manufacturer)} 추천 OID ${Object.keys(patch).length}개로 모두 덮어썼습니다.`,
    );
  };

  const handleSubmit = async () => {
    if (!form.machineName && !form.machineDescription) {
      addNotification("장비명 또는 설명 중 하나는 필수입니다.");
      return;
    }

    setSubmitting(true);
    try {
      const request: CreateMachineProductRequest = {
        machineType: form.machineType,
        manufacturer: form.manufacturer,
        machineName: form.machineName || null,
        machineDescription: form.machineDescription || null,
        protocol: form.protocol,
        machineStatusCommand: form.machineStatusCommand || null,
        machineRuntimeCommand: form.machineRuntimeCommand || null,
        machineTotalWorkloadCommand:
          form.machineTotalWorkloadCommand || null,
        suppliesNameCommand: form.suppliesNameCommand || null,
        suppliesTypeCommand: form.suppliesTypeCommand || null,
        suppliesMaxLevelCommand: form.suppliesMaxLevelCommand || null,
        suppliesCurrentLevelCommand:
          form.suppliesCurrentLevelCommand || null,
        trayNameCommand: form.trayNameCommand || null,
        paperNameCommand: form.paperNameCommand || null,
        paperWidthCommand: form.paperWidthCommand || null,
        paperHeightCommand: form.paperHeightCommand || null,
        trayMaxCapacityCommand: form.trayMaxCapacityCommand || null,
        trayCurrentCapacityCommand:
          form.trayCurrentCapacityCommand || null,
        jobNameCommand: form.jobNameCommand || null,
        jobStartTimeCommand: form.jobStartTimeCommand || null,
        jobEndTimeCommand: form.jobEndTimeCommand || null,
        jobStatusCommand: form.jobStatusCommand || null,
        alertSeverityCommand: form.alertSeverityCommand || null,
        alertGroupCommand: form.alertGroupCommand || null,
        alertCodeCommand: form.alertCodeCommand || null,
        alertDescriptionCommand: form.alertDescriptionCommand || null,
        alertTimeCommand: form.alertTimeCommand || null,
        errorStateCommand: form.errorStateCommand || null,
      };

      if (isEdit) {
        await adminMachineProductUseCase.update(Number(id), request);
        addNotification("수정되었습니다.");
      } else {
        await adminMachineProductUseCase.create(request);
        addNotification("등록되었습니다.");
      }

      navigate("/admin/machine-product");
    } catch (error) {
      addNotification(
        isEdit
          ? "수정에 실패했습니다."
          : "등록에 실패했습니다."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const noOidReason = NO_OID_REASONS[form.protocol];
  const needsNoOid = !!noOidReason;
  const presetFieldCount = Object.keys(OID_PRESETS[form.manufacturer] ?? {}).length;

  if (loading) {
    return (
      <Pod24AdminLayout>
        <div className="text-center py-10 text-[#888] text-[14px]">
          불러오는 중...
        </div>
      </Pod24AdminLayout>
    );
  }

  return (
    <Pod24AdminLayout>
      <NotificationsList notifications={notifications} />

      <div className="mb-6">
        <h1
          className="text-[22px] font-bold text-[#222]"
          style={{ fontFamily: "Pretendard" }}
        >
          {isEdit ? "장비 제품 상세" : "새 장비 제품 등록"}
        </h1>
        {isEdit && (
          <div className="flex justify-end mt-3">
            <Button
              size="sm"
              variant={editEnabled ? "gray" : "outline"}
              onClick={() => setEditEnabled(!editEnabled)}
            >
              {editEnabled ? "편집 종료" : "편집"}
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#e0e0e0] rounded-[12px] p-6">
        {/* 기본 정보 - 항상 표시 */}
        <div className="mb-6">
          <h2 className="text-[16px] font-bold text-[#222] mb-4">기본 정보</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] text-[#555] mb-1">
                장비 유형 *
              </label>
              <select
                className={`w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none ${!editEnabled ? "bg-[#f5f5f5] text-[#888]" : ""}`}
                value={form.machineType}
                disabled={!editEnabled}
                onChange={(e) =>
                  handleChange("machineType", e.target.value)
                }
              >
                {MACHINE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[13px] text-[#555] mb-1">
                제조사 *
              </label>
              <select
                className={`w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none ${!editEnabled ? "bg-[#f5f5f5] text-[#888]" : ""}`}
                value={form.manufacturer}
                disabled={!editEnabled}
                onChange={(e) => handleManufacturerChange(e.target.value)}
              >
                {MANUFACTURERS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[13px] text-[#555] mb-1">
                장비명
              </label>
              <input
                type="text"
                className={`w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none ${!editEnabled ? "bg-[#f5f5f5] text-[#888]" : ""}`}
                value={form.machineName}
                disabled={!editEnabled}
                onChange={(e) => handleChange("machineName", e.target.value)}
                placeholder="예: bizhub C3320i"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#555] mb-1">
                장비 설명
              </label>
              <input
                type="text"
                className={`w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none ${!editEnabled ? "bg-[#f5f5f5] text-[#888]" : ""}`}
                value={form.machineDescription}
                disabled={!editEnabled}
                onChange={(e) =>
                  handleChange("machineDescription", e.target.value)
                }
                placeholder="예: KONICA MINOLTA bizhub C3320i"
              />
            </div>

            <div>
              <label className="block text-[13px] text-[#555] mb-1">
                프로토콜 *
              </label>
              <select
                className={`w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none ${!editEnabled ? "bg-[#f5f5f5] text-[#888]" : ""}`}
                value={form.protocol}
                disabled={!editEnabled}
                onChange={(e) => handleChange("protocol", e.target.value)}
              >
                {PROTOCOLS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* OID 명령어 그룹들 */}
        {noOidReason && (
          <div className="mb-4 bg-[#EBF3FF] text-[#4285F4] border border-[#4285F4]/20 rounded-[8px] px-4 py-3 text-[13px]">
            {noOidReason}
          </div>
        )}

        {!needsNoOid && editEnabled && (
          <div className="mb-4 rounded-[8px] border border-[#4285F4]/20 bg-[#EBF3FF] px-4 py-3 text-[13px] text-[#4285F4]">
            {presetFieldCount > 0 ? (
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  {MANUFACTURER_LABELS[form.manufacturer] ?? form.manufacturer}{" "}
                  등록 제품 {OID_PRESET_SOURCE_COUNT[form.manufacturer] ?? 0}개에서 뽑은 추천 OID{" "}
                  {presetFieldCount}개가 있습니다. 제조사를 고르면 빈 칸만 자동으로 채워집니다.
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded-[6px] border border-[#4285F4] px-3 py-1.5 text-[12px] font-medium text-[#4285F4] hover:bg-[#4285F4] hover:text-white"
                  onClick={applyPresetOverwrite}
                >
                  추천값으로 모두 채우기
                </button>
              </div>
            ) : (
              <span>
                {MANUFACTURER_LABELS[form.manufacturer] ?? form.manufacturer} 는 아직 등록된 제품이
                없어 추천할 OID 가 없습니다. 직접 입력해 주세요.
              </span>
            )}
          </div>
        )}

        {!needsNoOid &&
          FIELD_GROUPS.slice(1).map((group) => (
            <div
              key={group.title}
              className="mb-4 border border-[#e0e0e0] rounded-[8px] overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between px-4 py-3 bg-[#FAFAFA] text-[14px] font-medium text-[#222] hover:bg-[#f0f0f0]"
                onClick={() => toggleGroup(group.title)}
              >
                {group.title}
                <svg
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    expandedGroups.includes(group.title) ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {expandedGroups.includes(group.title) && (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {group.fields.map((field) => (
                    <div key={field}>
                      <label className="block text-[13px] text-[#555] mb-1">
                        {FIELD_LABELS[field] || field}
                      </label>
                      <input
                        type="text"
                        className={`w-full border border-[#e0e0e0] rounded-[8px] px-3 py-2 text-[14px] focus:border-[#4285F4] focus:outline-none font-mono ${!editEnabled ? "bg-[#f5f5f5] text-[#888]" : ""}`}
                        value={form[field as keyof FormData] || ""}
                        disabled={!editEnabled}
                        onChange={(e) =>
                          handleChange(
                            field as keyof FormData,
                            e.target.value
                          )
                        }
                        placeholder="OID (예: 1.3.6.1.2.1.43...)"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

        {/* 제출 버튼 */}
        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/machine-product")}
          >
            {editEnabled && isEdit ? "취소" : "돌아가기"}
          </Button>
          {editEnabled && (
            <Button onClick={handleSubmit} loading={submitting}>
              {isEdit ? "수정" : "등록"}
            </Button>
          )}
        </div>
      </div>
    </Pod24AdminLayout>
  );
};

export default MachineProductForm;